// store/archiveSlice.js
import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  archiveFiles: {
    data: [],
    meta: {
      currentPage: 1,
      pageSize: 10,
      totalItems: 0,
      totalPages: 1,
      hasNext: false,
      hasPrevious: false,
    },
  },
  loading: false,
  error: null,
};

const slice = createSlice({
  name: 'archiveFile',
  initialState,
  reducers: {
    startLoadingfile: (state) => {
      state.loading = true;
      state.error = null;
    },
    getfileSuccess: (state, action) => {
      // expecting payload: { data: [...], meta: { ... } }
      state.archiveFiles.data = action.payload.data || [];
      state.archiveFiles.meta = action.payload.meta || initialState.archiveFiles.meta;
      state.loading = false;
      state.error = null;
    },
    hasError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },

    // handy optimistic updates
    addFileSuccess: (state, action) => {
      state.archiveFiles.data.unshift(action.payload);
    },
    updateFileSuccess: (state, action) => {
      const idx = state.archiveFiles.data.findIndex((f) => f._id === action.payload._id);
      if (idx !== -1) state.archiveFiles.data[idx] = action.payload;
    },
    deleteFileSuccess: (state, action) => {
      state.archiveFiles.data = state.archiveFiles.data.filter((f) => f._id !== action.payload);
    }
  },
});

export const {
  startLoadingfile,
  getfileSuccess,
  hasError,
  addFileSuccess,
  updateFileSuccess,
  deleteFileSuccess,
} = slice.actions;

export default slice.reducer;

/**
 * Thunk (plain thunk) to fetch archive files (paginated)
 * params.filters may include startDate, endDate, page, limit, sortBy, sortDir
 *
 * Usage:
 * dispatch(getArchiveFiles({ accessToken, filters: { page:1, limit:10 } }))
 */
export const getArchiveFiles = ({ accessToken, filters = {} }) => {
  return async (dispatch) => {
    dispatch(startLoadingfile());
    try {
      const base = process.env.REACT_APP_BACKEND_API || '';
      // build query string safely
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.page) params.append('page', filters.page);
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortDir) params.append('sortDir', filters.sortDir);
      if (filters.searchTerm) params.append('searchTerm', filters.searchTerm);

      const url = `${base}/get-all-archive-files?${params.toString()}`;
      const headers = {
        'Content-Type': 'application/json',
        Authorization: accessToken,
      };

      const res = await fetch(url, { headers });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        dispatch(hasError(err.error || 'Failed to fetch files'));
        return;
      }
      const body = await res.json();
      // controller returns: { success: true, data: files, meta }
      dispatch(getfileSuccess({ data: body.data || [], meta: body.meta || {} }));
    } catch (error) {
      dispatch(hasError(error.message || 'Network error'));
    }
  };
};
